import { useCallback, useRef } from "react";
import { getEQM1020_R01List, exportEQM1020_R01Excel } from "./Api_EQM1020_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { downloadExcel } from "src/utils/blob/blobUtil";
import { useEQM1020_R01Store, defaultEQM1020_R01Filter, EQM1020_R01Filter } from "./Store_EQM1020_R01";
import { EQM1020_R01Dto } from "./Dto_EQM1020_R01";

interface EQM1020_R01ContentProps {
    setMessage: (message: string) => void;
}

export const Content_EQM1020_R01 = (props: EQM1020_R01ContentProps) => {
    const {
        setMessage,
    } = props;

    const { 
        EQM1020_R01Filter, setEQM1020_R01Filter,
        EQM1020_R01PastFilter, setEQM1020_R01PastFilter,
        EQM1020_R01List, setEQM1020_R01List, 
        selectedEQM1020_R01List,
        setAllCount,
        initData,
    } = useEQM1020_R01Store();
    const { setLoadingSpinner } = useLoadingSpinnerStore();

    useFetchActionbarAuth("EQM1020_R01");

    // 동시 중복 요청 방지 플래그
    const isFetchingRef = useRef(false);

    const isSameDate = (a?: Date, b?: Date) => {
        if (!a && !b) return true;
        if (!a || !b) return false;
        return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    };

    const isSameFilter = (a: EQM1020_R01Filter, b: EQM1020_R01Filter) => {
        return (
            a.compCd === b.compCd &&
            a.plcaddressid === b.plcaddressid &&
            isSameDate(a.baseDate, b.baseDate)
        );
    };

    const fetchData = useCallback(async () => {
        if (isFetchingRef.current) return; // 중복 호출 방지
        isFetchingRef.current = true;
        setLoadingSpinner(true);
        try {
            // 필터 비교: 같으면 더보기, 다르면 초기화 후 새조회
            const same = isSameFilter(EQM1020_R01Filter, EQM1020_R01PastFilter);
            const nextListLength = same ? EQM1020_R01List.length : 0;
            // console.log('[EQM1020] fetchData same?', same, 'currentLen:', EQM1020_R01List.length);

            // 화면 즉시 초기화: 필터 변경 시 기존 리스트 비움
            if (!same && EQM1020_R01List.length > 0) {
                setEQM1020_R01List([]);
            }

            const response = await getEQM1020_R01List(
                '',
                EQM1020_R01Filter,
                nextListLength
            );

            const rawData = response.data.data as EQM1020_R01Dto[];
            
            // 서버에서 반환된 데이터가 없으면 안내
            if ( rawData.length === 0) {
                // 동일 조건으로 추가 조회한 경우: 더보기 결과 없음 메시지
                if (same) { /* empty */ } else {
                    // 신규 조건으로 조회했는데 결과가 없는 경우
                    setEQM1020_R01List([]);
                    setEQM1020_R01PastFilter(EQM1020_R01Filter);
                    showWarning('조회된 데이터가 없습니다.');
                }
                setLoadingSpinner(false);
                return;
            }

            // 필터 동일: append, 변경: replace
            const newList = same ? [...EQM1020_R01List, ...rawData] : [...rawData];
            setEQM1020_R01List(newList);

            setEQM1020_R01PastFilter(EQM1020_R01Filter);
            ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}건)`);
        } catch (error) {
            console.error('데이터 조회 실패:', error);
            showWarning('데이터 조회에 실패했습니다.');
        } finally {
            setLoadingSpinner(false);
            isFetchingRef.current = false;
        }
    }, [setLoadingSpinner, isSameFilter, EQM1020_R01Filter, EQM1020_R01PastFilter, EQM1020_R01List, setEQM1020_R01List, setEQM1020_R01PastFilter, setMessage]);
    
    const handleExcelClick = useCallback(async () => {
        try {
            const response = await exportEQM1020_R01Excel(
                '',
                EQM1020_R01Filter,
                0  // listLength를 0으로 설정하여 항상 새로 조회
            );

            const binaryString = atob(response.data.data);
            const byteArray = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                byteArray[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([byteArray]);
            downloadExcel(blob, '설비데이터 수집 분단위 집계 조회-excel.xlsx');
        } catch (error) {
            console.error('엑셀 다운로드 실패:', error);
            showWarning('엑셀 다운로드에 실패했습니다.');
        }
    }, [EQM1020_R01Filter]);

    // 🔧 추가: 초기화 핸들러
    const handleInitClick = useCallback(() => {
        initData();
        setMessage('데이터가 초기화되었습니다.');
    }, [initData, setMessage]);
    
    return {
        fetchData,
        handleExcelClick,
        handleInitClick,
    };
};
